import { useState } from 'react';
import { Loader2, Package, FileText, Flag, Zap } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { PhotoSlot } from '@/components/flatbed/PhotoSlot';
import { PageError } from '@/components/PageError';
import { useFocusScroll } from '@/components/common/UploadStatusRow';

export interface DocumentsTabProps {
  tripId: string;
  status: string;
  isAdmin: boolean;
  highlightMissing?: ('pickup_truck_photo' | 'pickup_receipt_photo' | 'wasika' | 'delivery_truck_photo')[];
  // Photo state from the trip detail GET payload
  pickupTruckUrl: string | null;
  pickupReceiptUrl: string | null;
  wasikaUrl: string | null;
  deliveryTruckUrl: string | null;
  deliveryDocumentUrl: string | null;
  pickupUploadedAt: string | null;
  wasikaUploadedAt: string | null;
  deliveryUploadedAt: string | null;
  forceAuditCount: number;
  onChange: () => void;
}

/**
 * PR-S1 MAY 11 SWEEP — Documents tab on FlatbedTripDetailPage.
 * Groups the 5 photo slots into 3 workflow stages with optional admin
 * "Force advance →" buttons that POST /transition with { force: true,
 * forceReason }.
 */
export function DocumentsTab(props: DocumentsTabProps) {
  const {
    tripId, status, isAdmin, highlightMissing,
    pickupTruckUrl, pickupReceiptUrl, wasikaUrl, deliveryTruckUrl, deliveryDocumentUrl,
    pickupUploadedAt, wasikaUploadedAt, deliveryUploadedAt,
    forceAuditCount, onChange,
  } = props;

  const [forceOpen, setForceOpen] = useState<null | { from: string; to: string; label: string }>(null);
  const [forceReason, setForceReason] = useState('');
  const [forcing, setForcing] = useState(false);
  const [forceError, setForceError] = useState<string | null>(null);
  // PR-CHIPS MAY 11 — react to ?focus=loaded-truck etc. from the chip click.
  useFocusScroll();

  function openForce(from: string, to: string, label: string) {
    setForceOpen({ from, to, label });
    setForceReason('');
    setForceError(null);
  }

  async function submitForce() {
    if (!forceOpen) return;
    if (forceReason.trim().length < 2) { setForceError('Reason required'); return; }
    setForcing(true);
    setForceError(null);
    try {
      await apiClient.post(`/api/v1/flatbed/trips/${tripId}/transition`, {
        newStatus: forceOpen.to,
        force: true,
        forceReason,
      });
      setForceOpen(null);
      onChange();
    } catch (e) { setForceError(extractErrorMessage(e)); }
    finally { setForcing(false); }
  }

  const hl = (slot: NonNullable<DocumentsTabProps['highlightMissing']>[number]) =>
    !!highlightMissing?.includes(slot);

  const canForceLoaded     = isAdmin && status === 'at_pickup';
  const canForceInTransit  = isAdmin && status === 'loaded';
  const canForceUnloaded   = isAdmin && status === 'at_delivery';

  return (
    <div className="space-y-6">
      {forceAuditCount > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-800 dark:text-amber-300 inline-flex items-center gap-2">
          <Flag className="h-3.5 w-3.5" />
          <strong>{forceAuditCount}</strong> admin force-advance override(s) recorded on this trip.
        </div>
      )}

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium inline-flex items-center gap-2"><Package className="h-4 w-4 text-muted-foreground" /> At pickup</h3>
          {canForceLoaded && (
            <Button size="sm" variant="outline" onClick={() => openForce('at_pickup', 'loaded', 'Mark loaded (skip pickup photos)')} className="gap-1">
              <Zap className="h-3 w-3" /> Force loaded →
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div id="upload-loaded-truck">
            <PhotoSlot tripId={tripId} slot="pickup-truck"   label="Loaded truck photo" required
                       filledUrl={pickupTruckUrl} uploadedAt={pickupUploadedAt}
                       onChange={onChange} highlight={hl('pickup_truck_photo')} />
          </div>
          <div id="upload-sender-receipt">
            <PhotoSlot tripId={tripId} slot="pickup-receipt" label="Sender receipt photo" required
                       filledUrl={pickupReceiptUrl} uploadedAt={pickupUploadedAt}
                       onChange={onChange} highlight={hl('pickup_receipt_photo')} />
          </div>
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium inline-flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" /> Wasika (consignment note)</h3>
          {canForceInTransit && (
            <Button size="sm" variant="outline" onClick={() => openForce('loaded', 'in_transit', 'Start trip (skip wasika)')} className="gap-1">
              <Zap className="h-3 w-3" /> Force in_transit →
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div id="upload-wasika">
            <PhotoSlot tripId={tripId} slot="wasika" label="Wasika PDF" required
                       accept="application/pdf"
                       filledUrl={wasikaUrl} uploadedAt={wasikaUploadedAt}
                       onChange={onChange} highlight={hl('wasika')} />
          </div>
          <div className="rounded-lg border border-border p-3 text-xs text-muted-foreground space-y-1">
            <div className="font-medium">How wasika works</div>
            <ol className="list-decimal pl-5 space-y-1">
              <li>Admin uploads Bayan receipt (in WasikaModal)</li>
              <li>System generates consignment PDF</li>
              <li>Upload the generated PDF here to unlock <code>in_transit</code></li>
            </ol>
          </div>
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium inline-flex items-center gap-2"><Flag className="h-4 w-4 text-muted-foreground" /> At delivery</h3>
          {canForceUnloaded && (
            <Button size="sm" variant="outline" onClick={() => openForce('at_delivery', 'unloaded', 'Mark unloaded (skip delivery photo)')} className="gap-1">
              <Zap className="h-3 w-3" /> Force unloaded →
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div id="upload-delivery-truck">
            <PhotoSlot tripId={tripId} slot="delivery-truck"    label="Delivery truck photo" required
                       filledUrl={deliveryTruckUrl} uploadedAt={deliveryUploadedAt}
                       onChange={onChange} highlight={hl('delivery_truck_photo')} />
          </div>
          <div id="upload-delivery-document">
            <PhotoSlot tripId={tripId} slot="delivery-document" label="Delivery document (optional)"
                       filledUrl={deliveryDocumentUrl} uploadedAt={deliveryUploadedAt}
                       onChange={onChange} />
          </div>
        </div>
      </section>

      <Sheet open={!!forceOpen} onOpenChange={(o) => !o && setForceOpen(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{forceOpen?.label}</SheetTitle>
            <SheetDescription>
              Admin override. Captured in audit log with timestamp + user.
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-6 space-y-3 text-sm">
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
              You are bypassing the photo/wasika gate. State will advance from
              <strong> {forceOpen?.from}</strong> to <strong>{forceOpen?.to}</strong> without the
              normally-required document. The override is permanently logged.
            </div>
            <div className="space-y-1">
              <Label>Reason *</Label>
              <textarea
                value={forceReason}
                onChange={(e) => setForceReason(e.target.value)}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base"
                placeholder="e.g. driver lost the receipt; photos will follow tomorrow"
              />
            </div>
            {forceError && <PageError error={forceError} />}
          </div>
          <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setForceOpen(null)} disabled={forcing}>Cancel</Button>
            <Button onClick={submitForce} disabled={forcing || forceReason.trim().length < 2}>
              {forcing ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Confirm force-advance
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
